import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { reportError } from '@/lib/errorReporting';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', { error, errorInfo });
    }
    reportError(error, {
      scope: 'react-error-boundary',
      tags: { component_stack: errorInfo?.componentStack ? 'present' : 'absent' },
    });
    // Auto-recover from stale chunk imports after a redeploy.
    const msg = error?.message || '';
    const isChunkError =
      /Importing a module script failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /ChunkLoadError/i.test(error?.name || '');
    if (isChunkError) {
      const KEY = '__chunkReloadAt';
      const last = Number(sessionStorage.getItem(KEY) || '0');
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  private handleHome = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.assign('/');
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-background px-6"
          role="alert"
        >
          <div className="text-center max-w-md w-full">
            <AlertTriangle
              className="mx-auto h-10 w-10 text-muted-foreground/40 mb-4"
              strokeWidth={1.5}
              aria-hidden
            />
            <h1
              className="text-2xl font-semibold mb-2"
            >
              Something went sideways
            </h1>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              {this.state.error?.message ||
                'An unexpected error occurred while loading this page.'}
            </p>

            <div className="flex items-center justify-center gap-2">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <RefreshCw className="h-4 w-4" aria-hidden />
                Try again
              </button>
              <button
                onClick={this.handleHome}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card text-sm font-medium hover:bg-muted transition-colors"
              >
                <Home className="h-4 w-4" aria-hidden />
                Go home
              </button>
            </div>

            {isDev && this.state.error?.stack && (
              <details className="mt-6 text-left text-xs whitespace-pre-wrap bg-muted/40 p-3 rounded-lg max-h-48 overflow-auto">
                <summary className="cursor-pointer mb-2 text-muted-foreground">
                  Stack trace (dev only)
                </summary>
                {this.state.error.stack}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
